import Big from 'big.js';
import * as Immutable from 'immutable';
import isEmpty from 'lodash.isempty';
import Moment from 'moment';
import { Moment as MomentType } from 'moment';
import { extendMoment } from 'moment-range';

import { promotionDisplayRules } from '../../conf/domain';
import { Api, BVScheduledTransport } from '../common/model';
import type { FormContent } from './components/Form';

export interface Slot {
  value: string;
  isUnavailable: boolean;
}

Big.RM = 1; // HALF_UP

const moment = extendMoment(Moment);

export const isErrorField = (forceValidation?: boolean, value?: any) => {
  const isNotEmpty = !!value && value.toString().length > 0;
  return forceValidation && !isNotEmpty;
};

export const displayDate = (date?: Date) => (date ? moment(date).format('DD/MM/YYYY') : '');

export const displayDecimal = (number: number, withoutDecimal: boolean = false) => {
  const value = new Big(number).toFixed(2).toString();
  if (withoutDecimal) return value.replace('.00', '');

  return value;
};

export const getSiteWithId = (sites: Array<Api.Site>, id?: string) => sites.find(s => s.id.toString() === id);

export const displaySite = (sites: Array<Api.Site>, id?: string) => {
  const site = getSiteWithId(sites, id);
  return site ? site.name : '';
};

export const filterObjectFields = <O extends object>(object: O, fieldNames: Array<keyof O>) => {
  if (!object || !fieldNames) return null;

  const entries = fieldNames
    .map(fieldName => [fieldName, object[fieldName]])
    .filter(([, value]) => value !== undefined);

  return Object.fromEntries(entries);
};

export const hasEmptyValue = (object: Object) =>
  Object.values(object).some(value => value === undefined || value === null || value.toString().length === 0);

export const isObjectEmpty = (objectName: object) => Object.keys(objectName).length === 0;

export type SerializedBooking = Partial<
  Record<`${'start' | 'end'}${Capitalize<keyof FormContent>}`, FormContent[keyof FormContent]>
>;

export const serializeBooking = (departure: FormContent, arrival: FormContent) => {
  const capitalizeFirst = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

  const undefinedIfEmpty = (value?: any) => {
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'string' && value.length > 0) return value;
    if (value instanceof Date) return displayDate(value);
    if (value instanceof Object && !isEmpty(value)) return value;
    return undefined;
  };

  const prependToField = (target: FormContent, prefix: 'start' | 'end'): SerializedBooking =>
    Object.entries(target).reduce((acc, [key, value]) => {
      const capitalized = capitalizeFirst(key) as Capitalize<keyof FormContent>;
      const newKey = `${prefix}${capitalized}` as const;
      acc[newKey] = undefinedIfEmpty(value);
      return acc;
    }, {} as SerializedBooking);

  const start = prependToField(departure, 'start');
  const end = prependToField(arrival, 'end');

  return { start, end };
};

export const mapStateFromForm = ($form: JQuery) => {
  const findValueWithKey = (key: string) => {
    const value = $form.find<HTMLInputElement>(`input[name="${key}"]`).val();
    if (value !== undefined && value.length > 0) return value;

    return undefined;
  };

  const findJsonWithKey = (key: string) => {
    const value = $form.find<HTMLInputElement>(`input[name="${key}"]`).val();
    if (value !== undefined && value.length > 0) return JSON.parse(value);

    return {};
  };

  const toDate = (value?: string) => {
    if (value) return moment(value, 'DD/MM/YYYY').toDate();

    return undefined;
  };

  const departure = {
    site: findValueWithKey('startSite'),
    checkpoint: findValueWithKey('startCheckpoint'),
    date: toDate(findValueWithKey('startDate')),
    hour: findValueWithKey('startHour'),
    tripNumber: findValueWithKey('startTripNumber'),
    tripDetail: findJsonWithKey('startTripDetail'),
  };

  const arrival = {
    site: findValueWithKey('endSite'),
    checkpoint: findValueWithKey('endCheckpoint'),
    date: toDate(findValueWithKey('endDate')),
    hour: findValueWithKey('endHour'),
    tripNumber: findValueWithKey('endTripNumber'),
    tripDetail: findJsonWithKey('endTripDetail'),
  };

  const getOptions = () =>
    Immutable.Set(
      (findValueWithKey('services') || '')
        .split(',')
        .map(Number)
        .filter(s => s > 0),
    );

  const voucher = findValueWithKey('voucher');
  const business = findValueWithKey('business');
  const travelType = findValueWithKey('travelType');

  const getWorker = () => {
    const $input = $form.find('input[name="business"]');
    if ($input && $input.val() && $input[0].hasAttribute('data-id')) {
      return {
        id: $input.attr('data-id'),
        business: $input.attr('data-name'),
        name: $input.attr('data-worker'),
        isWorker: true,
      };
    }

    return undefined;
  };

  const information = {
    lastName: findValueWithKey('lastName'),
    firstName: findValueWithKey('firstName'),
    email: findValueWithKey('email'),
    phoneNumber: findValueWithKey('phoneNumber'),
    company: findValueWithKey('company'),
    address: {
      firstLine: findValueWithKey('address.firstLine'),
      secondLine: findValueWithKey('address.secondLine'),
      zipCode: findValueWithKey('address.zipCode'),
      city: findValueWithKey('address.city'),
      country: findValueWithKey('address.country'),
    },
    vehicle: {
      kind: findValueWithKey('vehicle.kind'),
      color: findValueWithKey('vehicle.color'),
    },
    expensesTitle: findValueWithKey('expensesTitle'),
  };

  return {
    booking: { departure, arrival },
    cart: {
      options: getOptions(),
      voucher,
      travelType,
      business,
      worker: getWorker(),
    },
    information,
  };
};

export const getAvailableTimesFromSlots = (slots: Api.SiteSlots, start?: MomentType, end?: MomentType): Array<Slot> => {
  if (end?.isBefore(start)) return [];

  const rangeStart = moment(slots.startTime, 'HH:mm');
  const rangeEnd = moment(slots.endTime, 'HH:mm');
  const range = moment.range(rangeStart, rangeEnd);

  const unavailableSet = new Set(slots.unavailables);

  const allSlots = Array.from(range.by('minutes', { step: slots.step })).map((slot, index) => ({
    slot,
    value: slot.format('HH:mm'),
    isUnavailable: unavailableSet.has(index),
  }));

  if (!start?.isValid()) return allSlots;

  return allSlots.filter(({ slot }) => slot.isBetween(start, end, null, '[]'));
};

export const getAvailableSlot = (
  tripDetail: Pick<BVScheduledTransport, 'arrivalTime'> | Record<string, never>,
  slots: Array<Slot>,
) => {
  const arrivalTime = moment(tripDetail.arrivalTime, 'DD/MM/YYYY HH:mm');
  const minArrivalTime = arrivalTime.clone().subtract(2, 'm');
  const maxArrivalTime = arrivalTime.clone().add(2, 'm');

  return slots.find(t => {
    const momentSlot = moment(t.value, 'HH:mm');
    const slotValue = arrivalTime.clone().hour(momentSlot.hour()).minute(momentSlot.minute());
    return slotValue.isBetween(minArrivalTime, maxArrivalTime, 'minutes', '[]');
  });
};

export const isDisplayablePromotion = (
  totalWithoutPromotion: number | undefined,
  total: number,
  displayRules = promotionDisplayRules,
) => {
  if (totalWithoutPromotion) {
    const isValidAmount = totalWithoutPromotion - total >= displayRules.minPromotionAmount;
    const isValidPercentage =
      Number(((totalWithoutPromotion - total) / totalWithoutPromotion).toFixed(2)) >= displayRules.minPromotionCoeff;

    return isValidAmount && isValidPercentage;
  }
  return false;
};

export const calcDiscount = (totalWithoutPromotion: number | undefined, total: number) => {
  if (totalWithoutPromotion) {
    return Number((((totalWithoutPromotion - total) / totalWithoutPromotion) * 100).toFixed(0));
  }
  return 0;
};

export const stringNotEmpty = (s: string | undefined) => s && s !== '';
